import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertTriangle, Check, FileText, RefreshCw, Share2, Sparkles, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import authedFetch from '../../lib/authedFetch';
import ShareReportCardDialog from '../ShareReportCardDialog';
import { cn } from '../../lib/utils';
import { JOURNEY_STAGES } from '../../lib/journeyStages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IMPACT_TONE = {
  High: 'bg-rose-50 text-rose-700 border-rose-200',
  Medium: 'bg-amber-50 text-amber-700 border-amber-200',
  Low: 'bg-slate-50 text-slate-600 border-slate-200',
};

/**
 * FrictionPanel — Missing Middle Friction Analysis.
 *
 * AI-ranked barriers preventing capability from converting into value.
 * On mount it only shows a cached diagnosis (never auto-spends an LLM
 * generation); the user explicitly triggers "Diagnose Friction" and
 * can force a re-diagnosis at any time.
 */
export function FrictionPanel({ orgId, cohortId, getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [reportCaseId, setReportCaseId] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const fetchFriction = useCallback(async ({ force = false, cachedOnly = false } = {}) => {
    if (orgId == null) return;
    if (!cachedOnly) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgId) params.append('organization_id', orgId);
      if (cohortId && cohortId !== 'all') params.append('cohort_id', cohortId);
      if (force) params.append('force', 'true');
      if (cachedOnly) params.append('cached_only', 'true');
      const res = await authedFetch(`${API_URL}/api/people-engine/friction?${params}`, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setData(d.available === false ? null : d);
      } else if (!cachedOnly) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate friction analysis');
      }
    } catch {
      if (!cachedOnly) toast.error('Failed to generate friction analysis');
    }
    setLoading(false);
  }, [orgId, cohortId, getHeaders]);

  useEffect(() => {
    setData(null);
    setReportCaseId(null);
    fetchFriction({ cachedOnly: true });
  }, [fetchFriction]);

  // One click: AI-ranked barriers + conversion chain → CFO-ready
  // Missing Middle brief in the Business Case Builder (the existing
  // Executive Report Card pipeline).
  const postToReportCard = async () => {
    if (!data || posting || reportCaseId) return;
    setPosting(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/friction/post-to-report-card`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(orgId ? { organization_id: orgId } : {}),
          cohort_id: cohortId && cohortId !== 'all' ? cohortId : 'all',
        }),
      });
      if (res.ok) {
        const d = await res.json();
        setReportCaseId(d.case_id);
        toast.success('Missing Middle brief posted to Business Case Builder');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to post brief');
      }
    } catch {
      toast.error('Failed to post brief');
    }
    setPosting(false);
  };

  const stageColor = (label) => JOURNEY_STAGES.find((s) => s.label === label)?.color || '#64748b';

  return (
    <>
    <Card className="border-2 border-amber-100 h-full" data-testid="friction-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Missing Middle Friction Analysis
        </CardTitle>
        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Biggest barriers to value</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <RefreshCw className="h-5 w-5 animate-spin text-amber-500" />
            <span className="text-sm text-slate-500 text-center">
              Diagnosing where capability fails to convert into value…
            </span>
          </div>
        ) : data ? (
          <div className="space-y-3">
            {data.summary && (
              <p className="text-xs text-slate-600 leading-relaxed border-l-2 border-amber-300 pl-2.5" data-testid="friction-summary">
                {data.summary}
              </p>
            )}
            <ol className="space-y-2.5" data-testid="friction-barrier-list">
              {(data.barriers || []).map((b, i) => (
                <li
                  key={`${b.name}-${i}`}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                  data-testid={`friction-barrier-${i}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-sm font-semibold text-slate-800">{b.name}</span>
                    <span className={cn('ml-auto rounded-full border px-2 py-0.5 text-[10px] font-bold', IMPACT_TONE[b.impact] || IMPACT_TONE.Low)}>
                      {b.impact}
                    </span>
                  </div>
                  {b.stage && (
                    <span
                      className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                      style={{ backgroundColor: stageColor(b.stage) }}
                    >
                      {b.stage}
                    </span>
                  )}
                  {b.rationale && <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{b.rationale}</p>}
                  {b.intervention && (
                    <p className="text-[11px] text-slate-600 mt-1 flex items-start gap-1.5">
                      <Wrench className="h-3 w-3 text-cyan-700 mt-0.5 flex-shrink-0" />
                      <span>{b.intervention}</span>
                    </p>
                  )}
                </li>
              ))}
            </ol>
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="friction-report-card-row">
              <Button
                size="sm"
                variant={reportCaseId ? 'outline' : 'default'}
                className="gap-1.5"
                onClick={postToReportCard}
                disabled={posting || !!reportCaseId}
                data-testid="friction-post-to-report-card-btn"
                title="Turn this friction diagnosis + conversion chain into a CFO-ready Missing Middle brief in the Business Case Builder"
              >
                {reportCaseId
                  ? <><Check className="h-3.5 w-3.5 text-emerald-600" /> Posted</>
                  : posting
                    ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Drafting brief…</>
                    : <><FileText className="h-3.5 w-3.5" /> Post to Executive Report Card</>}
              </Button>
              {reportCaseId && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                    onClick={() => setShareOpen(true)}
                    data-testid="friction-share-cfo-btn"
                    title="Mint a signed, read-only public link — your CFO or board can read the brief without a login"
                  >
                    <Share2 className="h-3.5 w-3.5" /> Share with your CFO
                  </Button>
                  <a
                    href="/business-case-builder"
                    className="text-xs text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
                    data-testid="friction-open-report-card-link"
                  >
                    Open in Business Case Builder →
                  </a>
                </>
              )}
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
              <span className="text-[10px] text-slate-400">
                {data.cached ? 'Cached · ' : ''}
                {data.generated_at ? new Date(data.generated_at).toLocaleString() : ''} · {data.model || 'AI'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 text-[11px] text-amber-700 hover:bg-amber-50 h-7 px-2"
                onClick={() => fetchFriction({ force: true })}
                data-testid="friction-regenerate-btn"
              >
                <RefreshCw className="h-3 w-3" /> Re-diagnose
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <p className="text-sm text-slate-500 max-w-[240px]">
              AI-ranked diagnosis of the barriers blocking capability from converting into business value.
            </p>
            <Button
              size="sm"
              className="gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => fetchFriction()}
              data-testid="friction-generate-btn"
            >
              <Sparkles className="h-3.5 w-3.5" /> Diagnose Friction
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
    <ShareReportCardDialog
      open={shareOpen}
      onOpenChange={setShareOpen}
      report={{ case_id: reportCaseId, skill_name: 'Missing Middle Brief' }}
    />
    </>
  );
}
